import { useBehavior, useCheckBox } from '@libs/hooks';
import { TInputData, TWidget } from '@libs/types';
import { camelCaseToKebabCase, getFieldName } from '@libs/utils';
import clsx from 'clsx';
import React, { Activity, useId } from 'react';
import { TCheckboxOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './checkBoxSwitcher.module.css';

type TSwitcherProps<TD, TO> =
    | {
          isWidget: true;
          widget: TWidget<TD, TO>;
      }
    | ({
          isWidget: false;
          uid?: string;
          value: boolean;
          handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
      } & Pick<TInputData, 'tabIndex' | 'messages' | 'label'> &
          TCheckboxOption);

const Switcher = (props: TSwitcherProps<TInputData, TCheckboxOption>) => {
    const { isWidget } = props;
    const finalWidget = !isWidget
        ? ({
              uid: props.uid ?? '',
              data: {
                  value: props.value,
                  tabIndex: props.tabIndex,
                  label: props.label,
                  messages: props.messages,
              },
              options: {
                  isDisabled: props.isDisabled,
                  margin: props.margin,
                  autofill: props.autofill,
              },
          } as TWidget<TInputData, TCheckboxOption>)
        : props.widget;

    const id = useId();
    const data = finalWidget.data ?? {};
    const options = finalWidget.options ?? {};
    const label = data.messages?.label || data.label;
    const { handleChange, isChecked } = useCheckBox(finalWidget);
    const { isDisabled, isHidden } = useBehavior(finalWidget);

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div style={getInlineStyles({ margin: options.margin })} data-test={finalWidget.uid}>
                <div className={clsx(styles.switchWrapper, isDisabled && styles.disabled)}>
                    <input
                        data-test={`input_${finalWidget.uid}`}
                        className={styles.hide}
                        id={id}
                        type="checkbox"
                        name={getFieldName(options.autofill)}
                        autoComplete={camelCaseToKebabCase(options.autofill)}
                        disabled={isDisabled}
                        checked={isChecked}
                        onChange={(e) => {
                            if (isWidget) {
                                handleChange(e);
                            } else {
                                props.handleChange(e);
                            }
                        }}
                        tabIndex={data.tabIndex}
                    />
                    <div className={styles.switchInputBlock}>
                        {label && <TextCommon isWidget={false} content={label} options={{ textColor: 'grey_72_lc', margin: { right: 24 } }} />}
                        <label className={clsx(styles.switchControl, isDisabled && styles.disabled)} htmlFor={id} />
                    </div>
                </div>
            </div>
        </Activity>
    );
};
export { Switcher };
